import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { TFunction } from 'i18next';
import { useManagers, useRegisterBOUser } from '../mutations/useRegister';
import { useModal } from './useDialog';
import { UserRole, USER_ROLES, ROLE_OPTIONS_CONFIG } from '../constants/roles';
import useLocalization from './useLocalization';
import { useRoles } from './useRoles';
import { useAuth } from '@neotech-solutions-org/neofusion-fe-shared';

const createBOUserFieldSchemas = (t: TFunction) => ({
  email: z
    .string({ error: t('register.validation.requiredField') })
    .pipe(z.email(t('register.validation.invalidEmail')))
    .optional(),
  username: z
    .string({ error: t('register.validation.requiredField') })
    .min(2, t('register.validation.minLength', { min: 2 }))
    .optional(),
  password: z
    .string({ error: t('register.validation.requiredField') })
    .min(8, t('register.validation.passwordTooShort')),
  firstName: z
    .string({ error: t('register.validation.requiredField') })
    .min(2, t('register.validation.minLength', { min: 2 })),
  lastName: z
    .string({ error: t('register.validation.requiredField') })
    .min(2, t('register.validation.minLength', { min: 2 })),
  groupName: z.string({ error: t('register.validation.requiredField') }).optional(),
  agentId: z.string({ error: t('register.validation.requiredField') }).optional(),
  managerId: z.string({ error: t('register.validation.requiredField') }).optional(),
});

const getBOUserRegisterSchema = (t: TFunction, isAgent: boolean, isAdmin: boolean) => {
  const fieldSchemas = createBOUserFieldSchemas(t);
  return z.object(fieldSchemas).superRefine((data, ctx) => {
    if (data.groupName === USER_ROLES.AGENT && !data.managerId && isAdmin) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t('register.validation.requiredField'),
        path: ['managerId'],
      });
    }
    if (isAgent) {
      if (!data.username) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('register.validation.requiredField'),
          path: ['username'],
        });
      }
    } else {
      if (!data.email) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('register.validation.requiredField'),
          path: ['email'],
        });
      }
    }
  });
};

export type BOUserFormData = z.infer<ReturnType<typeof getBOUserRegisterSchema>>;

export const useCreateBOUsersForm = () => {
  const { role: userRole, isAdmin, isAgent } = useRoles();
  const { userId } = useAuth();
  const { closeModal } = useModal();
  const { t } = useLocalization();

  const registerBOUserSchema = getBOUserRegisterSchema(t, isAgent, isAdmin);
  const registrationEndPoint = isAgent ? '/customer' : '/agent';
  const { mutateAsync: registerBOUser, isPending: isLoading } = useRegisterBOUser(registrationEndPoint);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    setValue,
  } = useForm<BOUserFormData>({
    resolver: zodResolver(registerBOUserSchema),
    defaultValues: {
      groupName: (ROLE_OPTIONS_CONFIG[userRole]?.[0]?.value as UserRole) || undefined,
    },
  });

  const selectedRole = watch('groupName');
  const selectedManagerId = watch('managerId');
  const enableSelectManagers = selectedRole === USER_ROLES.AGENT && isAdmin;
  const { data: managersData } = useManagers(enableSelectManagers);

  const localizedRoleOptions =
    ROLE_OPTIONS_CONFIG[userRole]?.map((option) => ({
      ...option,
      label: t(`roles.${option.label.toLowerCase()}`),
    })) || [];

  const managerOptions =
    managersData?.map((manager) => ({
      value: manager.id,
      label: manager.fullName,
    })) || [];

  const userIdDisplay = (userId && userId.length >= 6 ? userId.slice(-6) : userId || '------').toUpperCase();

  const onSubmit = (data: BOUserFormData) => {
    const finalData = isAgent ? { ...data, agentId: userId } : { ...data, managerId: selectedManagerId ?? userId };
    registerBOUser(finalData, {
      onSuccess: closeModal,
    });
  };

  return {
    t,
    control,
    handleSubmit,
    errors,
    isSubmitting,
    isLoading,
    isAdmin,
    isAgent,
    selectedRole,
    selectedManagerId,
    enableSelectManagers,
    localizedRoleOptions,
    managerOptions,
    userIdDisplay,
    closeModal,
    onSubmit,
    setValue,
  };
};
